import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import AgoraRTC, {
  IAgoraRTCClient,
  ILocalAudioTrack,
  ILocalVideoTrack,
  IAgoraRTCRemoteUser,
  UID,
} from 'agora-rtc-sdk-ng';
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  IconButton,
  Tooltip,
  Typography,
} from '@mui/material';
import MicOffIcon from '@mui/icons-material/MicOff';
import MicIcon from '@mui/icons-material/Mic';
import VideocamOffIcon from '@mui/icons-material/VideocamOff';
import VideocamIcon from '@mui/icons-material/Videocam';
import CallEndIcon from '@mui/icons-material/CallEnd';
import PersonIcon from '@mui/icons-material/Person';
import { getAgoraToken } from '../../services/NetworkServices';
import './AgoraCallModal.css';

type CallMode = 'audio' | 'video';

type Props = {
  open: boolean;
  onClose: () => void;
  onEndCall: () => void;
  channelName: string;
  uid: number;
  mode: CallMode;
  appId: string | undefined;
  token?: string | null;
  tokenUid?: number | null;
  calleeName?: string;
  calleeProfilePic?: string;
};

const formatCallDuration = (totalSeconds: number): string => {
  const hrs = Math.floor(totalSeconds / 3600);
  const mins = Math.floor((totalSeconds % 3600) / 60);
  const secs = totalSeconds % 60;
  const pad = (n: number): string => String(n).padStart(2, '0');
  return hrs > 0 ? `${pad(hrs)}:${pad(mins)}:${pad(secs)}` : `${pad(mins)}:${pad(secs)}`;
};

const AgoraCallModal: React.FC<Props> = ({
  open,
  onClose,
  onEndCall,
  channelName,
  uid,
  mode,
  appId,
  token,
  tokenUid,
  calleeName,
  calleeProfilePic,
}) => {
  const [client, setClient] = useState<IAgoraRTCClient | null>(null);
  const [localAudioTrack, setLocalAudioTrack] = useState<ILocalAudioTrack | null>(null);
  const [localVideoTrack, setLocalVideoTrack] = useState<ILocalVideoTrack | null>(null);
  const [remoteUsers, setRemoteUsers] = useState<IAgoraRTCRemoteUser[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string>('');
  const [micEnabled, setMicEnabled] = useState(true);
  const [cameraEnabled, setCameraEnabled] = useState(mode === 'video');
  const [joinedUid, setJoinedUid] = useState<UID | null>(null);
  const [callSeconds, setCallSeconds] = useState(0);
  const [connected, setConnected] = useState(false);
  const [peerJoined, setPeerJoined] = useState(false);
  const [avatarError, setAvatarError] = useState(false);

  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const localAudioTrackRef = useRef<ILocalAudioTrack | null>(null);
  const localVideoTrackRef = useRef<ILocalVideoTrack | null>(null);

  const resolvedUid: UID = useMemo(() => uid, [uid]);
  const useToken = process.env.REACT_APP_AGORA_USE_TOKEN !== 'false';
  const debugAgora = process.env.NODE_ENV !== 'production';
  const logAgora = useCallback(
    (label: string, payload?: unknown) => {
      if (!debugAgora) return;
      // eslint-disable-next-line no-console
      console.log(`[AgoraCall] ${label}`, payload ?? '');
    },
    [debugAgora]
  );

  const callHasVideo =
    cameraEnabled ||
    remoteUsers.some((u) => u.hasVideo || !!u.videoTrack);

  const visibleRemoteUsers = remoteUsers.filter((user, index, users) => {
    const firstIndex = users.findIndex((item) => String(item.uid) === String(user.uid));
    const isSelf = joinedUid != null && String(user.uid) === String(joinedUid);
    const hasRenderableTrack =
      user.hasVideo || !!user.videoTrack || user.hasAudio || !!user.audioTrack;
    return firstIndex === index && !isSelf && hasRenderableTrack;
  });

  useEffect(() => {
    localAudioTrackRef.current = localAudioTrack;
  }, [localAudioTrack]);

  useEffect(() => {
    localVideoTrackRef.current = localVideoTrack;
  }, [localVideoTrack]);

  useEffect(() => {
    if (!open) return;
    setCameraEnabled(mode === 'video');
  }, [open, mode]);

  // Parent can set open=false (e.g. socket call:ended) without Dialog onClose — still clear timer state.
  useEffect(() => {
    if (open) return;
    if (timerRef.current) {
      clearInterval(timerRef.current);
      timerRef.current = null;
    }
    setCallSeconds(0);
    setPeerJoined(false);
    setConnected(false);
  }, [open]);

  useEffect(() => {
    if (!peerJoined && connected && visibleRemoteUsers.length > 0) {
      setPeerJoined(true);
    }
  }, [connected, visibleRemoteUsers.length, peerJoined]);

  useEffect(() => {
    if (peerJoined && !error) {
      timerRef.current = setInterval(() => {
        setCallSeconds((prev) => prev + 1);
      }, 1000);
    }
    return () => {
      if (timerRef.current) clearInterval(timerRef.current);
    };
  }, [peerJoined, error]);

  const playRemoteVideo = useCallback(
    (user: IAgoraRTCRemoteUser, retry = 0) => {
      if (!user.videoTrack) return;
      const containerId = `agora-remote-${user.uid}`;
      const el = document.getElementById(containerId);
      if (el) {
        logAgora('playing remote video', {
          remoteUid: user.uid,
          containerId,
          hasVideoTrack: !!user.videoTrack,
        });
        user.videoTrack.play(el);
        return;
      }
      if (retry < 20) {
        setTimeout(() => playRemoteVideo(user, retry + 1), 120);
      }
    },
    [logAgora]
  );

  useEffect(() => {
    if (!open) return;
    if (!appId && !useToken) {
      setError('Missing Agora App ID. Set REACT_APP_AGORA_APP_ID in your env.');
      return;
    }
    if (!channelName) {
      setError('Missing channel name.');
      return;
    }

    let mounted = true;
    const rtcClient = AgoraRTC.createClient({ mode: 'rtc', codec: 'vp8' });
    setClient(rtcClient);

    const handleUserUpdate = () => {
      if (!mounted) return;
      logAgora(
        'remote users update',
        rtcClient.remoteUsers.map((user) => ({
          uid: user.uid,
          hasAudio: user.hasAudio,
          hasVideo: user.hasVideo,
          hasAudioTrack: !!user.audioTrack,
          hasVideoTrack: !!user.videoTrack,
        }))
      );
      setRemoteUsers([...rtcClient.remoteUsers]);
    };

    const syncRemoteUsers = async () => {
      const currentRemoteUsers = [...rtcClient.remoteUsers];
      logAgora(
        'sync remote users',
        currentRemoteUsers.map((user) => ({
          uid: user.uid,
          hasAudio: user.hasAudio,
          hasVideo: user.hasVideo,
          hasAudioTrack: !!user.audioTrack,
          hasVideoTrack: !!user.videoTrack,
        }))
      );
      await Promise.all(
        currentRemoteUsers.map(async (user) => {
          if (user.hasAudio && !user.audioTrack) {
            logAgora('subscribing remote audio', { remoteUid: user.uid });
            await rtcClient.subscribe(user, 'audio');
          }
          if (user.hasVideo && !user.videoTrack) {
            logAgora('subscribing remote video', { remoteUid: user.uid });
            await rtcClient.subscribe(user, 'video');
          }
          if (user.audioTrack) {
            user.audioTrack.play();
          }
          if (user.videoTrack) {
            requestAnimationFrame(() => playRemoteVideo(user));
          }
        })
      );
      handleUserUpdate();
    };

    rtcClient.on('user-published', async (user, mediaType) => {
      logAgora('user published', {
        remoteUid: user.uid,
        mediaType,
        hasAudio: user.hasAudio,
        hasVideo: user.hasVideo,
      });
      await rtcClient.subscribe(user, mediaType);
      handleUserUpdate();
      if (mediaType === 'audio') {
        user.audioTrack?.play();
      }
      if (mediaType === 'video') {
        requestAnimationFrame(() => playRemoteVideo(user));
      }
    });

    rtcClient.on('user-joined', (user) => {
      logAgora('user joined', { remoteUid: user.uid });
      handleUserUpdate();
    });
    rtcClient.on('user-info-updated', (uid) => {
      logAgora('user info updated', { remoteUid: uid });
      handleUserUpdate();
      const updatedUser = rtcClient.remoteUsers.find(
        (user) => String(user.uid) === String(uid)
      );
      if (updatedUser?.videoTrack) {
        requestAnimationFrame(() => playRemoteVideo(updatedUser));
      }
    });
    rtcClient.on('user-unpublished', (user, mediaType) => {
      logAgora('user unpublished', { remoteUid: user.uid, mediaType });
      handleUserUpdate();
    });
    rtcClient.on('user-left', (user) => {
      logAgora('user left', { remoteUid: user.uid });
      handleUserUpdate();
    });

    const start = async () => {
      setLoading(true);
      try {
        let joinToken: string | null = token || null;
        let joinChannelName = channelName;
        let joinUid = tokenUid && tokenUid > 0 ? tokenUid : resolvedUid;
        let joinAppId = appId;

        logAgora('start call', {
          requestedChannelName: channelName,
          requestedUid: resolvedUid,
          mode,
          hasPrefetchedToken: !!token,
          prefetchedTokenUid: tokenUid,
          appId,
        });

        if (useToken && !joinToken) {
          const tokenResponse = await getAgoraToken({
            channelName,
            uid: Number(resolvedUid),
          });

          if (!tokenResponse.data?.status || !tokenResponse.data?.data) {
            throw new Error(tokenResponse.data?.message || 'Failed to get Agora token.');
          }

          joinToken = tokenResponse.data.data.token;
          joinChannelName =
            channelName || tokenResponse.data.data.channelName || channelName;
          joinUid =
            tokenResponse.data.data.uid && Number(tokenResponse.data.data.uid) > 0
              ? tokenResponse.data.data.uid
              : resolvedUid;
          joinAppId = tokenResponse.data.data.appId || appId;
          logAgora('token response', {
            responseChannelName: tokenResponse.data.data.channelName,
            responseUid: tokenResponse.data.data.uid,
            responseAppId: tokenResponse.data.data.appId,
            expiresAt: tokenResponse.data.data.expiresAt,
          });
        }

        if (!joinAppId) {
          throw new Error('Missing Agora App ID.');
        }

        logAgora('joining channel', {
          joinChannelName,
          joinUid,
          joinAppId,
          usingToken: !!joinToken,
        });

        const actualJoinedUid = await rtcClient.join(
          joinAppId,
          joinChannelName,
          joinToken,
          joinUid
        );
        if (!mounted) return;
        setJoinedUid(actualJoinedUid);
        logAgora('joined channel', {
          joinChannelName,
          requestedUid: joinUid,
          actualJoinedUid,
        });
        await syncRemoteUsers();

        if (mode === 'video') {
          const [audioTrack, videoTrack] =
            await AgoraRTC.createMicrophoneAndCameraTracks();
          if (!mounted) return;
          setLocalAudioTrack(audioTrack);
          setLocalVideoTrack(videoTrack);
          await rtcClient.publish([audioTrack, videoTrack]);
          logAgora('published local video/audio', {
            localTrackEnabled: videoTrack.enabled,
            localAudioEnabled: audioTrack.enabled,
          });
          await syncRemoteUsers();
        } else {
          const audioTrack = await AgoraRTC.createMicrophoneAudioTrack();
          if (!mounted) return;
          setLocalAudioTrack(audioTrack);
          await rtcClient.publish([audioTrack]);
          logAgora('published local audio', {
            localAudioEnabled: audioTrack.enabled,
          });
          await syncRemoteUsers();
        }
        setLoading(false);
        setConnected(true);
      } catch (err: unknown) {
        if (!mounted) return;
        setLoading(false);
        logAgora('call error', err);
        const message = err instanceof Error ? err.message : 'Failed to start call.';
        setError(message);
      }
    };

    start();

    return () => {
      mounted = false;
      rtcClient.removeAllListeners();
      const releaseMedia = async (): Promise<void> => {
        try {
          const audio = localAudioTrackRef.current;
          const video = localVideoTrackRef.current;
          audio?.stop();
          audio?.close();
          video?.stop();
          video?.close();
          localAudioTrackRef.current = null;
          localVideoTrackRef.current = null;
          await rtcClient.leave();
        } catch (cleanupErr: unknown) {
          logAgora('effect cleanup (leave / tracks)', cleanupErr);
        }
      };
      void releaseMedia();
    };
  }, [
    open,
    appId,
    channelName,
    resolvedUid,
    mode,
    useToken,
    playRemoteVideo,
    token,
    tokenUid,
    logAgora,
  ]);

  useEffect(() => {
    if (!localVideoTrack || !cameraEnabled) return;
    let cancelled = false;
    const playLocal = (retry = 0) => {
      if (cancelled) return;
      const el = document.getElementById('agora-local-video');
      if (el) {
        localVideoTrack.play(el);
        logAgora('local video playing in DOM', { retry });
        return;
      }
      if (retry < 30) {
        setTimeout(() => playLocal(retry + 1), 50);
      }
    };
    playLocal();
    return () => {
      cancelled = true;
    };
  }, [localVideoTrack, cameraEnabled, logAgora]);

  useEffect(() => {
    if (!open) return;
    visibleRemoteUsers.forEach((user) => {
      playRemoteVideo(user);
    });
  }, [visibleRemoteUsers, open, playRemoteVideo]);

  const handleLeave = async () => {
    try {
      localAudioTrack?.stop();
      localAudioTrack?.close();
      localVideoTrack?.stop();
      localVideoTrack?.close();
      await client?.leave();
    } finally {
      setLocalAudioTrack(null);
      setLocalVideoTrack(null);
      setRemoteUsers([]);
      setJoinedUid(null);
      setError('');
      setMicEnabled(true);
      setCameraEnabled(mode === 'video');
      setCallSeconds(0);
      setConnected(false);
      setPeerJoined(false);
      setAvatarError(false);
      if (timerRef.current) clearInterval(timerRef.current);
      onClose();
    }
  };

  const handleEndCall = async () => {
    onEndCall();
    await handleLeave();
  };

  const toggleMic = async () => {
    if (!localAudioTrack) return;
    const next = !micEnabled;
    await localAudioTrack.setEnabled(next);
    setMicEnabled(next);
  };

  const toggleVideo = async () => {
    if (!client) return;
    if (localVideoTrack) {
      try {
        await client.unpublish(localVideoTrack);
        localVideoTrack.stop();
        localVideoTrack.close();
      } catch (err: unknown) {
        logAgora('toggleVideo unpublish error', err);
      }
      setLocalVideoTrack(null);
      setCameraEnabled(false);
    } else {
      try {
        const videoTrack = await AgoraRTC.createCameraVideoTrack();
        await client.publish(videoTrack);
        setLocalVideoTrack(videoTrack);
        setCameraEnabled(true);
        logAgora('toggleVideo enabled', { trackEnabled: videoTrack.enabled });
      } catch (err: unknown) {
        logAgora('toggleVideo enable error', err);
      }
    }
  };

  const renderCalleeAvatar = (size: number): React.ReactNode => {
    if (calleeProfilePic && !avatarError) {
      return (
        <img
          src={calleeProfilePic}
          alt={calleeName || 'Callee'}
          className="agora-callee-avatar-img"
          style={{ width: size, height: size, borderRadius: '50%', objectFit: 'cover' }}
          onError={() => setAvatarError(true)}
        />
      );
    }
    return <PersonIcon className="agora-audio-avatar-icon" />;
  };

  const renderAudioOnlyView = (): React.ReactNode => (
    <Box className="agora-audio-center">
      <Box className="agora-audio-avatar-ring">
        <Box className="agora-audio-avatar-ring-pulse" />
        <Box className="agora-audio-avatar">
          {renderCalleeAvatar(120)}
        </Box>
      </Box>
      {calleeName && (
        <Typography className="agora-callee-name">{calleeName}</Typography>
      )}
      <Typography className="agora-audio-status">
        {loading ? 'Connecting...' : peerJoined ? 'On call' : 'Ringing...'}
      </Typography>
      {peerJoined && (
        <Typography className="agora-audio-timer">
          {formatCallDuration(callSeconds)}
        </Typography>
      )}
    </Box>
  );

  return (
    <Dialog open={open} onClose={handleLeave} fullScreen>
      <Box className="agora-call-root">
        {/* Header */}
        <Box className="agora-call-header">
          <Box className="agora-call-header-left">
            <Box
              className={`agora-call-mode-badge ${callHasVideo ? 'video' : 'audio'}`}
            >
              {callHasVideo ? (
                <VideocamIcon fontSize="small" />
              ) : (
                <MicIcon fontSize="small" />
              )}
              <span>{callHasVideo ? 'Video' : 'Audio'}</span>
            </Box>
            <Typography className="agora-call-title">
              {calleeName || (callHasVideo ? 'Video Call' : 'Audio Call')}
            </Typography>
          </Box>
          {peerJoined && (
            <Typography className="agora-call-timer">
              {formatCallDuration(callSeconds)}
            </Typography>
          )}
        </Box>

        {/* Body */}
        {error ? (
          <Box className="agora-call-error">
            <Box className="agora-error-card">
              <Typography className="agora-error-title">Connection Error</Typography>
              <Typography className="agora-error-message">{error}</Typography>
              <Button
                onClick={handleEndCall}
                variant="contained"
                className="agora-error-btn"
                startIcon={<CallEndIcon />}
              >
                End Call
              </Button>
            </Box>
          </Box>
        ) : (
          <Box className="agora-call-body">
            {loading && (
              <Box className="agora-call-loading">
                <CircularProgress size={28} sx={{ color: '#ffffff' }} />
                <Typography>Connecting...</Typography>
              </Box>
            )}

            {!callHasVideo && !loading ? (
              renderAudioOnlyView()
            ) : (
              <Box className="agora-video-grid">
                {cameraEnabled && (
                  <Box className="agora-video-card local">
                    <Box id="agora-local-video" className="agora-video-surface" />
                    <Box className="agora-video-label">You</Box>
                  </Box>
                )}
                {visibleRemoteUsers.map((user) => (
                  <Box key={String(user.uid)} className="agora-video-card remote-wrapper">
                    <Box
                      id={`agora-remote-${user.uid}`}
                      className="agora-video-surface"
                    />
                    {!user.videoTrack && (
                      <Box className="agora-video-placeholder">
                        <Box className="agora-placeholder-avatar">
                          {renderCalleeAvatar(48)}
                        </Box>
                        <Typography className="agora-placeholder-text">
                          {calleeName || 'Participant joined'}
                        </Typography>
                      </Box>
                    )}
                  </Box>
                ))}
              </Box>
            )}
          </Box>
        )}

        {/* Controls */}
        {!error && (
          <Box className="agora-call-controls-wrapper">
            <Box className="agora-call-controls">
              <Tooltip title={micEnabled ? 'Mute' : 'Unmute'} placement="top" arrow>
                <IconButton
                  onClick={toggleMic}
                  className={`agora-call-control ${!micEnabled ? 'active-off' : ''}`}
                >
                  {micEnabled ? <MicIcon /> : <MicOffIcon />}
                </IconButton>
              </Tooltip>

              <Tooltip title="Toggle Video" placement="top" arrow>
                <IconButton onClick={toggleVideo} className="agora-call-video-toggle">
                  {cameraEnabled ? <VideocamIcon /> : <VideocamOffIcon />}
                </IconButton>
              </Tooltip>

              <Tooltip title="End Call" placement="top" arrow>
                <IconButton onClick={handleEndCall} className="agora-call-end">
                  <CallEndIcon />
                </IconButton>
              </Tooltip>
            </Box>
          </Box>
        )}
      </Box>
    </Dialog>
  );
};

export { AgoraCallModal };
export default AgoraCallModal;
